import genAI from './geminiClient';

/**
 * Analyzes post content and determines if it matches keyword criteria using Gemini
 * @param {string} postContent - Content of the post to analyze
 * @param {string[]} targetKeywords - Keywords to match against
 * @param {string} matchType - 'any' or 'all' keywords must match
 * @returns {Promise<Object>} Match analysis result
 */
export async function analyzePostForKeywords(postContent, targetKeywords, matchType = 'any') {
  try {
    const keywordsList = targetKeywords?.join(', ');
    
    const prompt = `Analyze if this post is relevant to these keywords/topics: ${keywordsList}

Post content: "${postContent}"

Match type: ${matchType === 'all' ? 'ALL keywords must be relevant' : 'ANY keyword can be relevant'}

Consider synonyms, related concepts, and context. Provide your analysis in JSON format:
{
  "isMatch": <boolean>,
  "matchedKeywords": ["keyword1", "keyword2", ...],
  "relevanceScore": <number between 0 and 1>,
  "reasoning": "<brief explanation>"
}`;

    const model = genAI?.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      generationConfig: {
        temperature: 0.3,
        topP: 0.8,
        topK: 32,
      }
    });

    const result = await model?.generateContent(prompt);
    const response = await result?.response;
    const text = response?.text()?.trim();
    
    // Extract JSON from response
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Invalid response format from Gemini');
    }
    
    return JSON.parse(jsonMatch?.[0]);
  } catch (error) {
    console.error('Error analyzing post for keywords with Gemini:', error);
    throw new Error('Failed to analyze post. Please try again.');
  }
}

/**
 * Generates an AI-powered comment for a post using Gemini
 * @param {Object} options - Comment generation options
 * @param {string} options.postContent - Content of the post to comment on
 * @param {string} options.commentStyle - Style of comment (supportive, insightful, question, humorous)
 * @param {string} options.brandVoice - Brand voice to maintain
 * @param {string[]} options.keywords - Keywords that triggered this comment
 * @param {number} options.maxLength - Maximum comment length
 * @returns {Promise<string>} Generated comment text
 */
export async function generateComment(options) {
  const { 
    postContent, 
    commentStyle = 'supportive', 
    brandVoice = 'professional', 
    keywords = [], 
    maxLength = 200 
  } = options;

  try {
    const keywordContext = keywords?.length > 0 
      ? `This post was identified because it relates to: ${keywords?.join(', ')}.` 
      : '';

    const styleGuide = {
      supportive: 'encouraging and positive',
      insightful: 'thoughtful and adds value',
      question: 'curious and engaging with a question',
      humorous: 'light-hearted and fun while staying professional'
    };

    const prompt = `Generate a ${styleGuide?.[commentStyle]} comment for this social media post.

Post content: "${postContent}"

${keywordContext}

Requirements:
- Brand voice: ${brandVoice}
- Maximum length: ${maxLength} characters
- Be authentic and human-like
- Add value to the conversation
- Avoid generic responses
- DO NOT use hashtags in comments
- Sound natural, not robotic

Generate only the comment text, no explanations or quotes.`;

    const model = genAI?.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      generationConfig: {
        temperature: 0.8,
        topP: 0.9,
        topK: 40,
        maxOutputTokens: 300
      }
    });

    const result = await model?.generateContent(prompt);
    const response = await result?.response;
    return response?.text()?.trim();
  } catch (error) {
    console.error('Error generating comment with Gemini:', error);
    throw new Error('Failed to generate comment. Please try again.');
  }
}

/**
 * Generates multiple comment variations using Gemini
 * @param {Object} options - Comment generation options
 * @param {number} variations - Number of variations to generate
 * @returns {Promise<Array>} Array of generated comments
 */
export async function generateCommentVariations(options, variations = 3) {
  try {
    const comments = await Promise.all(
      Array.from({ length: variations }, () => generateComment(options))
    );
    return comments;
  } catch (error) {
    console.error('Error generating comment variations with Gemini:', error);
    throw new Error('Failed to generate comment variations. Please try again.');
  }
}

/**
 * Analyzes post sentiment and engagement potential using Gemini
 * @param {string} postContent - Post content to analyze
 * @returns {Promise<Object>} Sentiment and engagement analysis
 */
export async function analyzePostSentiment(postContent) {
  try {
    const prompt = `Analyze this post for sentiment and engagement potential:

"${postContent}"

Provide your analysis in JSON format:
{
  "sentiment": "<positive/negative/neutral/mixed>",
  "emotionalTone": ["tone1", "tone2", ...],
  "engagementPotential": "<high/medium/low>",
  "recommendedAction": "<comment/skip/monitor>",
  "reasoning": "<brief explanation>"
}`;

    const model = genAI?.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      generationConfig: {
        temperature: 0.3,
        topP: 0.8,
        topK: 32,
      }
    });

    const result = await model?.generateContent(prompt);
    const response = await result?.response;
    const text = response?.text()?.trim();
    
    // Extract JSON from response
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Invalid response format from Gemini');
    }
    
    return JSON.parse(jsonMatch?.[0]);
  } catch (error) {
    console.error('Error analyzing post sentiment with Gemini:', error);
    throw new Error('Failed to analyze post sentiment. Please try again.');
  }
}

/**
 * Safety check for comment content using Gemini
 * Note: Gemini has built-in safety filters, but this provides additional analysis
 * @param {string} comment - Comment text to check
 * @returns {Promise<Object>} Safety analysis results
 */
export async function moderateComment(comment) {
  try {
    const prompt = `Analyze this comment for safety and appropriateness:

"${comment}"

Check for:
- Harassment or bullying
- Hate speech
- Sexually explicit content
- Dangerous or harmful content
- Spam or misleading information

Provide analysis in JSON format:
{
  "isSafe": <boolean>,
  "concerns": ["concern1", "concern2", ...],
  "recommendation": "<approve/review/reject>"
}`;

    const model = genAI?.getGenerativeModel({ 
      model: 'gemini-2.5-flash',
      generationConfig: {
        temperature: 0.1,
        topP: 0.5,
      },
      safetySettings: [
        {
          category: "HARM_CATEGORY_HARASSMENT",
          threshold: "BLOCK_LOW_AND_ABOVE"
        },
        {
          category: "HARM_CATEGORY_HATE_SPEECH",
          threshold: "BLOCK_LOW_AND_ABOVE"
        },
        {
          category: "HARM_CATEGORY_SEXUALLY_EXPLICIT",
          threshold: "BLOCK_LOW_AND_ABOVE"
        },
        {
          category: "HARM_CATEGORY_DANGEROUS_CONTENT",
          threshold: "BLOCK_LOW_AND_ABOVE"
        }
      ]
    });

    const result = await model?.generateContent(prompt);
    const response = await result?.response;
    const text = response?.text()?.trim();
    
    // Extract JSON from response
    const jsonMatch = text?.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      // If we can't parse the response, assume it's safe (Gemini's built-in filters would block unsafe content)
      return {
        isSafe: true,
        concerns: [],
        recommendation: 'approve',
        flagged: false
      };
    }
    
    const analysis = JSON.parse(jsonMatch?.[0]);
    return {
      ...analysis,
      flagged: !analysis?.isSafe
    };
  } catch (error) {
    console.error('Error moderating comment with Gemini:', error);
    // Default to safe if moderation fails
    return {
      isSafe: true,
      concerns: ['Moderation check failed'],
      recommendation: 'review',
      flagged: false
    };
  }
}